import { useMutation, useQuery } from "@apollo/client/react"
import { DateTime } from "luxon"
import { useEffect, useState } from "react"
import * as Yup from "yup"

import { type VenuePlanData } from "@/components/plan"
import { STATUS_CONFIRMED, STATUS_WALK_IN } from "@/constants/reservations"
import { useVenue } from "@/contexts/venue"
import { UPDATE_RESERVATION_REFETCH_QUERIES } from "@/graphql/dependencies"
import { CreateReservationDocument, GetVenueFullDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { formatTableName } from "@/shared/tables"
import { useStore } from "@/store"

export interface LocalNote {
  id: string
  note: string
  createdAt: string
}

export interface CustomerFormData {
  name: string
  email: string
  phone: string
}

export interface TableSlot {
  tableId: string
  tableName: string
  startTime: string
  endTime: string
}

export interface CreateReservationFormValues {
  tableSlots: TableSlot[]
  peopleAmount: string | number | null
  date: DateTime | null
  status: string
  customer: CustomerFormData
}

export type ReservationType = "reservation" | "walk_in"

const normalizePeopleAmount = (value: unknown): number | null => {
  if (value == null) return null
  if (typeof value === "string" && value.trim() === "") return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

const validationSchema = Yup.object({
  tableSlots: Yup.array()
    .of(
      Yup.object({
        tableId: Yup.string().required(),
        tableName: Yup.string().required(),
        startTime: Yup.string().required(),
        endTime: Yup.string().required(),
      }),
    )
    .min(1, i18n.t("app.forms.create_reservation.errors.table_required")),
  peopleAmount: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(1, i18n.t("app.forms.create_reservation.errors.people_amount_min")),
  status: Yup.string().required(i18n.t("app.forms.create_reservation.errors.status_required")),
  customer: Yup.object({
    name: Yup.string(),
    email: Yup.string().email(i18n.t("app.forms.create_reservation.errors.customer_email_invalid")),
    phone: Yup.string(),
  }),
})

export function useBookingState() {
  const { venue } = useVenue()
  const { showCreateReservationDialog, setShowCreateReservationDialog, currentDate, createReservationPrefilledValues } =
    useStore()

  const [fullScreen, setFullScreen] = useState(false)
  const [localNotes, setLocalNotes] = useState<LocalNote[]>([])
  const [_mapHasPendingChanges, setMapHasPendingChanges] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean | null>(null)

  const initialReservationType: ReservationType = createReservationPrefilledValues?.reservationType || "reservation"
  const [reservationType, setReservationType] = useState<ReservationType>(initialReservationType)

  const [createReservation, { loading: creating }] = useMutation(CreateReservationDocument, {
    refetchQueries: UPDATE_RESERVATION_REFETCH_QUERIES,
  })

  const { data: venueFullData } = useQuery(GetVenueFullDocument, {
    variables: { slug: venue.slug },
    skip: !showCreateReservationDialog,
  })

  const venuePlans = (venueFullData?.venue?.plans || []) as VenuePlanData[]
  const reservationBuffer = venueFullData?.venue?.reservationConfiguration?.reservationBuffer || 0

  useEffect(() => {
    if (showCreateReservationDialog) {
      setReservationType(createReservationPrefilledValues?.reservationType || "reservation")
    }
  }, [showCreateReservationDialog, createReservationPrefilledValues?.reservationType])

  const handleClose = () => {
    setShowCreateReservationDialog(false)
    setLocalNotes([])
    setReservationType("reservation")
    setError(null)
    setSuccess(null)
  }

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(handleClose, 2000)
      return () => clearTimeout(timeout)
    }
  }, [success])

  const handleAddNote = (note: string) => {
    setLocalNotes((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        note,
        createdAt: DateTime.now().toISO(),
      },
    ])
  }

  const getInitialTableSlots = (): TableSlot[] => {
    if (
      createReservationPrefilledValues?.tableId &&
      createReservationPrefilledValues?.startTime &&
      createReservationPrefilledValues?.endTime
    ) {
      const table = venue.tables.find((t: any) => t.id === createReservationPrefilledValues.tableId)
      const tableName = table
        ? formatTableName(table) || createReservationPrefilledValues.tableId
        : createReservationPrefilledValues.tableId
      return [
        {
          tableId: createReservationPrefilledValues.tableId,
          tableName,
          startTime: createReservationPrefilledValues.startTime,
          endTime: createReservationPrefilledValues.endTime,
        },
      ]
    }
    return []
  }

  const handleReservationTypeChange = (
    type: ReservationType,
    setFieldValue: (field: string, value: any) => Promise<any> | void,
  ) => {
    setReservationType(type)
    if (type === "walk_in") {
      void setFieldValue("status", STATUS_WALK_IN)
    } else {
      void setFieldValue("status", STATUS_CONFIRMED)
    }
  }

  const onSubmit = async (values: CreateReservationFormValues) => {
    setError(null)

    const peopleAmountValue = normalizePeopleAmount(values.peopleAmount)

    const result = await createReservation({
      variables: {
        peopleAmount: peopleAmountValue,
        tableSlots: values.tableSlots.map((slot) => ({
          tableId: slot.tableId,
          startTime: slot.startTime,
          endTime: slot.endTime,
        })),
        venueId: venue.id,
        customer: values.customer,
        status: values.status,
        notes: localNotes.map((n) => ({ note: n.note })),
      },
    })

    const resultSuccess = result?.data?.createReservation?.success

    if (resultSuccess) {
      setSuccess(true)
    } else {
      setError(i18n.t("app.reservation_preview.create_error"))
      console.error("Error creating reservation", result)
    }
  }

  const initialValues: CreateReservationFormValues = {
    tableSlots: getInitialTableSlots(),
    peopleAmount: "",
    date: currentDate,
    status: initialReservationType === "walk_in" ? STATUS_WALK_IN : STATUS_CONFIRMED,
    customer: {
      name: "",
      email: "",
      phone: "",
    },
  }

  return {
    venue,
    venuePlans,
    reservationBuffer,
    showCreateReservationDialog,
    fullScreen,
    setFullScreen,
    localNotes,
    handleAddNote,
    mapHasPendingChanges: _mapHasPendingChanges,
    setMapHasPendingChanges,
    error,
    success,
    creating,
    reservationType,
    handleReservationTypeChange,
    handleClose,
    onSubmit,
    initialValues,
    validationSchema,
  }
}
